import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Operation, PermissionDtoV2, UsersService } from 'src/api/v2';

import { AUTH_KEY } from 'src/constants/hook-keys';

import useCluster from 'src/features/cluster/hooks/use-cluster';
import useEnvironment from 'src/hooks/use-environment';
import { getFFOverride, useFFOverrideRevision } from './use-local-ff-overrides';

// The generated service is stateless, so one shared instance avoids an allocation per render.
const service = new UsersService();

export const MODULE_TYPES = [
  'AccessControl',
  'AccountSettings',
  'AnnualApplication',
  'Application',
  'Assignments',
  'ClusterMember',
  'Collaborators',
  'Core',
  'Course',
  'DevelopmentProject',
  'Events',
  'FF_AlumniTab',
  'FF_CompanyGoals',
  'FF_Experiment',
  'FF_Fabric',
  'FF_OneWayInFrontPageStatistics',
  'FF_SelfDeclaration',
  'FF_ServiceTemplates',
  'Financing',
  'FocusArea',
  'Fylkesvisning',
  'GreenPlatform',
  'Inkind',
  'Leads',
  'MainGoal',
  'MemberEconomy',
  'OneWayIn',
  'OneWayInCustomerCommunication',
  'OneWayInOpportunities',
  'Operation',
  'PortalProject',
  'Report',
  'ResourceBase',
  'SivaAdmin',
  'SivaCore',
  'Statistics',
  'UI_SivaBusinessGarden',
  'UI_SivaIncubator',
] as const;

export type ModuleTypes = (typeof MODULE_TYPES)[number];

type AccessType = {
  read: boolean;
  write: boolean;
};

type UseAuthProps = {
  enabled?: boolean;
};

function useAuth({ enabled }: UseAuthProps = { enabled: true }) {
  const { cluster } = useCluster();
  const ffRevision = useFFOverrideRevision();
  const { isDev } = useEnvironment();

  const query = useQuery<PermissionDtoV2>(
    [AUTH_KEY, { clusterId: cluster?.id }],
    async () => {
      return await service.getUserRolePermissionsV2(cluster!.id);
    },
    {
      enabled: !!cluster && enabled,
    },
  );

  const checkAccess = useCallback(
    (module: ModuleTypes): AccessType => {
      // Reference ffRevision so this callback is recreated whenever an override
      // changes, forcing consumers to re-read getFFOverride below.
      void ffRevision;

      if (isDev) {
        const override = getFFOverride(module);
        if (override !== null) {
          return { read: override, write: override };
        }
      }

      if (query.data) {
        const item = query.data.permissions[module];
        if (item !== undefined) {
          return {
            read: item !== Operation.None,
            write: item === Operation.Write,
          };
        }
      }

      return {
        read: false,
        write: false,
      };
    },
    [query.data, ffRevision, isDev],
  );

  return {
    ...query,

    checkAccess,
  };
}

export default useAuth;
